import { Maybe } from 'graphql/jsutils/Maybe';
import { AnalyticsTrackPropertiesInput } from '.cache/__types__';
import { CardDetailsFragment } from '@graphql/fragments/cards/core.graphql';
import { getCookieConsentForCategory } from '@modules/cookie-utils';
import { getCookie, setCookie } from 'cookies-next';
import { nanoid } from 'nanoid';
import { Card } from 'types';

export function toCardProperties(card: Card | undefined): Partial<AnalyticsTrackPropertiesInput> {
  if (!card) {
    return {};
  }
  return {
    card_id: card.cardId,
    card_org_id: card.orgId,
    card_user_id: card.userId,
    card_update_id: card.cardUpdateId,
  };
}

/** A helper function to map GraphQL card properties to card analytics properties */
export function mapGraphQLCardPropertiesToAnalyticsProperties(
  card: Maybe<CardDetailsFragment>
): Partial<AnalyticsTrackPropertiesInput> {
  if (!card) {
    return {};
  }
  return {
    card_id: card?.id,
    card_org_id: card?.workspace_id,
    card_user_id: card?.profile?.user_id,
  };
}

// Identification Helpers
export function cookieHostname(): string {
  const host = window.location.hostname;
  switch (true) {
    case host.includes('blinq.me'): {
      return 'blinq.me';
    }
    case host.includes('blnq.xyz'): {
      return 'blnq.xyz';
    }
    default:
      return host;
  }
}

export const CARD_PAGE_VIEWER_ID_KEY = 'blinq_viewer_id';

/** An ID that can be persisted between webflow, dash, and card worker. */
export function getViewerId(): string | null {
  return getCookie(CARD_PAGE_VIEWER_ID_KEY) ?? null;
}

/** Sets a cookie value across Blinq domains  */
export function setViewerId(value: string): void {
  // The viewer ID is only set if the user has given consent for analytics cookies
  const consentGiven = getCookieConsentForCategory('analytics');

  if (!consentGiven) {
    // Don't set as cookie if no consent is given.

    return;
  }

  const cookieExpirationDate = new Date(new Date().getTime() + 400 * 24 * 60 * 60 * 1000);

  setCookie(CARD_PAGE_VIEWER_ID_KEY, value, {
    expires: cookieExpirationDate,
    domain: cookieHostname(),
    path: '/',
  });
}

export const generateAnonymousId = (identifyEventAnonId?: string | null): string => {
  const viewerId = getViewerId();
  const anonymousId = viewerId ?? identifyEventAnonId ?? `anon_${nanoid()}`;
  if (viewerId !== anonymousId) {
    setViewerId(anonymousId);
  }
  return anonymousId;
};
